import { useState, useEffect, useRef } from "react";
import SearchBar from "./SearchBar";
import CharacterPanel from "./CharacterPanel";
import HistoryPanel from "./HistoryPanel";
import "./Search.css";
import { formatDistanceToNow } from "date-fns";
import itemsDB from "../items.json"; 
import { ItemTypeEnum, ItemTypeEnumToString } from "../enums/ItemTypeEnum";
import QueueBar from "./QueueBar";

function Search() {
  useEffect(()=>{ document.title = "Search"; },[]);
  const [character, setCharacter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [collapseRaidsSignal, setCollapseRaidsSignal] = useState(0);
  const [lastSearchTime, setLastSearchTime] = useState(0);
  // Simple request queue for character lookups with 429 backoff handling
  const queueRef = useRef([]); // { id, name, realm, attempts, scheduledAt, resolve, reject }
  const processingRef = useRef(false);
  const nextTimerRef = useRef(null);
  const [queueUi, setQueueUi] = useState({ total: 0, position: 0, nextAt: 0 });

  // Derive loading from processing or queued tasks
  const isBusy = (processingRef.current || (queueRef.current.length > 0));
  const loadingDerived = isBusy || loading;

  useEffect(() => {
    // Load history from localStorage
    const stored = localStorage.getItem("gc-history");
    if (stored) {
      try {
        // Parse and normalize history entries so gearScore is numeric and defaults to 0
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          const normalized = parsed
            .map((h) => {
              const copy = { ...h };
              // If gearScore is the string 'Unknown' or missing, default to 0
              if (copy.gearScore === 'Unknown' || copy.gearScore === undefined || copy.gearScore === null) copy.gearScore = 0;
              // If gearScore is a numeric string, coerce to number
              if (typeof copy.gearScore === 'string' && !isNaN(Number(copy.gearScore))) copy.gearScore = Number(copy.gearScore);
              // Ensure legacy entries have a timestamp so pruning behaves consistently
              if (!copy.ts) {
                copy.ts = copy.lastChecked || Date.now();
              }
              return copy;
            })
            // Remove entries with missing or placeholder name/realm
            .filter((h) => {
              const name = (h.name || '').trim();
              const realm = (h.realm || '').trim();
              if (!name || name.toLowerCase() === 'unknown') return false;
              if (!realm || realm.toLowerCase() === 'unknown') return false;
              // Do not automatically purge entries based on timestamp/age.
              return true;
            });
          // Merge any pinned characters (from localStorage) that may not be present
          // in the serialized history so pins remain visible after reloads.
          let merged = [...normalized];
          try {
            const pinnedRaw = localStorage.getItem('pinnedCharactersV2');
            if (pinnedRaw) {
              const pinnedObj = JSON.parse(pinnedRaw);
              if (pinnedObj && typeof pinnedObj === 'object') {
                for (const key of Object.keys(pinnedObj)) {
                  // Key format is expected to be 'name|realm'
                  if (!merged.find(h => `${(h.name||'')}|${(h.realm||'')}` === key)) {
                    const parts = key.split('|');
                    const name = (parts[0] || '').trim();
                    const realm = (parts[1] || '').trim();
                    if (name && realm) {
                      // Try to find full entry in normalized history
                      const fullEntry = normalized.find(h => (h.name||'').trim() === name && (h.realm||'').trim() === realm);
                      if (fullEntry) {
                        merged.unshift({ ...fullEntry, lastChecked: pinnedObj[key] || fullEntry.lastChecked || Date.now(), ts: pinnedObj[key] || fullEntry.ts || Date.now() });
                      } else {
                        merged.unshift({
                          name,
                          realm,
                          faction: 'Unknown',
                          class: 'Unknown',
                          level: 'Unknown',
                          race: 'Unknown',
                          gearScore: 0,
                          lastChecked: pinnedObj[key] || Date.now(),
                          ts: pinnedObj[key] || Date.now(),
                        });
                      }
                    }
                  }
                }
              }
            }
          } catch (_e) {
            // Ignore parsing errors for pinned map
          }
          setHistory(merged);
        } else {
          // Stored value is not an array; do not purge user's history.
          console.warn('gc-history in localStorage is not an array; leaving existing in-memory history unchanged.');
        }
      } catch (e) {
        // Instead of silently removing the user's history, back it up and try a best-effort salvage.
        console.warn('Corrupted history in localStorage. Backing up raw value and attempting salvage.', e);
        try {
          const backupKey = `gc-history-corrupted-${Date.now()}`;
          localStorage.setItem(backupKey, stored);
        } catch (_e) {
          // ignore backup failures
        }

        // Best-effort: try to extract a JSON array substring if possible
        try {
          const firstBracket = stored.indexOf('[');
          const lastBracket = stored.lastIndexOf(']');
          if (firstBracket !== -1 && lastBracket !== -1 && lastBracket > firstBracket) {
            const sub = stored.substring(firstBracket, lastBracket + 1);
            const maybe = JSON.parse(sub);
            if (Array.isArray(maybe)) {
              // Reuse the salvaged array (will still be normalized by outer logic)
              setHistory(maybe);
              return;
            }
          }
        } catch (_e) {
          // salvage failed
        }

  // Fallback: leave in-memory history unchanged so we do not purge the user's history.
  console.warn('Failed to salvage stored history; leaving original localStorage entry intact for manual recovery.');
      }
    }
  }, []);

  // Core function to perform a Warmane API fetch once. Throws errors with code and retryAfter when applicable.
  const fetchCharacterFromAPI = async (name, realm) => {
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
    try {
      const res = await fetch(`/api/character/${encodeURIComponent(name)}/${encodeURIComponent(realm)}/type`, { signal: controller.signal });
      const bodyText = await res.text();
      if (!res.ok) {
        if (res.status === 429) {
          const err = new Error("rate_limited");
          err.code = 429;
          // Parse Retry-After header (seconds or HTTP-date)
          const ra = res.headers.get('retry-after');
          let retryMs = 5000;
          if (ra) {
            const sec = parseFloat(ra);
            if (!isNaN(sec)) retryMs = Math.max(1000, Math.floor(sec * 1000));
            else {
              const dateMs = Date.parse(ra);
              if (!isNaN(dateMs)) retryMs = Math.max(1000, dateMs - Date.now());
            }
          }
          err.retryAfterMs = retryMs;
          throw err;
        } else if (res.status === 404) {
          const err = new Error(`Character "${name}" not found on realm "${realm}".`);
          err.code = 404;
          throw err;
        } else {
          const snippet = (bodyText || '').slice(0, 200);
          const err = new Error(snippet || "Unknown error from API");
          err.code = res.status || 'HTTP_ERROR';
          throw err;
        }
      }
      let data;
      try { data = bodyText ? JSON.parse(bodyText) : {}; }
      catch (e) {
        const snippet = (bodyText || '').slice(0, 200);
        const err = new Error(`Unexpected response from server (not JSON). ${snippet}`);
        err.code = 'BAD_JSON';
        throw err;
      }
      return data;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  // Helper: fetch and parse Warmane Armory summary HTML to extract enchants/gems, character stats, and secondary skills
  async function fetchArmoryExtras(characterName, realmName) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const url = `/armory/character/${encodeURIComponent(characterName)}/${encodeURIComponent(realmName)}/summary`;
      const res = await fetch(url, { signal: controller.signal, headers: { 'Accept': 'text/html' } });
      if (!res.ok) {
        throw new Error(`Armory HTTP ${res.status}`);
      }
      const html = await res.text();
      // Attempt to extract an embedded ModelViewer-style items array like:
      // items: [[1,64692],[3,65000],...]
      let modelViewerItems = null;
      try {
        const itemsRe = /items\s*:\s*(\[\s*\[[^\]]+\](?:\s*,\s*\[[^\]]+\]\s*)*\])/i;
        const im = html.match(itemsRe);
        if (im && im[1]) {
          try {
            const parsed = JSON.parse(im[1]);
            if (Array.isArray(parsed)) modelViewerItems = parsed;
          } catch (_e) {
            // If JSON.parse fails, ignore — it's non-critical
          }
        }
      } catch (_e) { /* ignore */ }
      // Parse item-slot anchors: <a ... rel="item=51277&ench=3817&gems=3628:3518:0">
      const itemAffixes = {};
      const itemRelRegex = /<a[^>]+href=["'][^"']*cavernoftime[^>]*item=(\d+)[^>]*rel=["']([^"']+)["'][^>]*>/gi;
      let m;
      while ((m = itemRelRegex.exec(html)) !== null) {
        const id = Number(m[1]);
        const relRaw = m[2];
        // Decode minimal HTML entities commonly found in rel attributes
        const rel = relRaw.replace(/&amp;/g, '&');
        const params = Object.create(null);
        try {
          rel.split('&').forEach(part => {
            const [k, v] = part.split('=');
            if (k) params[k] = v || '';
          });
        } catch(_e) {}
        const ench = params['ench'] ? Number(params['ench']) : undefined;
        const gems = params['gems'] ? params['gems'].split(':').map(g => (g && g !== '0') ? Number(g) : null) : undefined;
        const transmog = params['transmog'] ? Number(params['transmog']) : undefined;
        if (!isNaN(id)) {
          itemAffixes[id] = { ench, gems, rel, transmog };
        }
      }

      // Parse Character Stats block into up to three columns by finding each .stub block
      const statsColumns = [[], [], []];
      try {
        const startIdx = html.search(/<div\s+class=["']character-stats["'][^>]*>/i);
        if (startIdx !== -1) {
          // Take a generous slice after the character-stats container start to capture all stubs
          const slice = html.slice(startIdx, Math.min(html.length, startIdx + 20000));
          const stubRegex = /<div[^>]+class=["']stub[^"']*["'][\s\S]*?<\/div>/gi;
          let si = 0; let sm;
          while ((sm = stubRegex.exec(slice)) !== null && si < 3) {
            const block = sm[0];
            const text = block
              .replace(/<[^>]+>/g, '\n')
              .replace(/&nbsp;/g, ' ')
              .replace(/&amp;/g, '&')
              .replace(/\s+\n/g, '\n')
              .replace(/\n{2,}/g, '\n')
              .trim();
            const lines = text.split(/\n+/).filter(Boolean).map(s=>s.trim());
            statsColumns[si] = lines;
            si++;
          }
        }
      } catch(_e) {}

      // Parse Secondary Skills (Cooking, First Aid, Fishing, Riding) from the same HTML
      // Prefer a DOM approach when available for robustness; fallback to regex/context window matching
      const secondarySkills = [];
      try {
        if (typeof window !== 'undefined' && typeof window.DOMParser === 'function') {
          const dp = new window.DOMParser();
          const doc = dp.parseFromString(html, 'text/html');
          const names = ['First Aid', 'Cooking', 'Fishing', 'Riding'];
          const found = new Map(); // name -> { skill, cap }
          // Search any text node occurrences and try to find a sibling with X/Y
          const walker = doc.createTreeWalker(doc.body || doc, NodeFilter.SHOW_TEXT, null);
          let node;
          while ((node = walker.nextNode())) {
            const t = (node.nodeValue || '').trim();
            if (!t) continue;
            for (const nm of names) {
              if (t.toLowerCase().includes(nm.toLowerCase())) {
                // Explore nearby DOM (parent and siblings) for a value like "123/450" or "300/300"
                const candidates = [];
                const accumulateText = (el) => (el ? (el.textContent || '') : '');
                const parent = node.parentElement;
                if (parent) {
                  candidates.push(accumulateText(parent));
                  if (parent.parentElement) candidates.push(accumulateText(parent.parentElement));
                  if (parent.nextElementSibling) candidates.push(accumulateText(parent.nextElementSibling));
                  if (parent.previousElementSibling) candidates.push(accumulateText(parent.previousElementSibling));
                }
                const block = [t, ...candidates].join(' ');
                const m2 = block.match(/(\d{1,3})\s*\/\s*(\d{2,3})/);
                if (m2) {
                  const skill = parseInt(m2[1], 10) || 0;
                  const cap = parseInt(m2[2], 10) || 0;
                  const prev = found.get(nm);
                  if (!prev || skill > prev.skill) found.set(nm, { skill, cap });
                }
              }
            }
          }
          found.forEach((val, key) => { secondarySkills.push({ name: key, skill: val.skill, cap: val.cap || 450 }); });
        }
      } catch(_e) {}

      // Regex fallback if DOM parsing didn't find anything
      try {
        if (!secondarySkills.length) {
          const names = ['First Aid', 'Cooking', 'Fishing', 'Riding'];
          const lowerHtml = html.toLowerCase();
          for (const nm of names) {
            const idx = lowerHtml.indexOf(nm.toLowerCase());
            if (idx !== -1) {
              const start = Math.max(0, idx - 120);
              const end = Math.min(html.length, idx + 160);
              const context = html.slice(start, end).replace(/<[^>]+>/g, ' ');
              const m2 = context.match(/(\d{1,3})\s*\/\s*(\d{2,3})/);
              if (m2) {
                const skill = parseInt(m2[1], 10) || 0;
                const cap = parseInt(m2[2], 10) || 0;
                secondarySkills.push({ name: nm, skill, cap });
              }
            }
          }
        }
      } catch(_e) {}

      // Parse Primary Professions caps from the same HTML
      // Covers: Alchemy, Blacksmithing, Enchanting, Engineering, Herbalism, Inscription, Jewelcrafting, Leatherworking, Mining, Skinning, Tailoring
      const primaryCaps = [];
      try {
        if (typeof window !== 'undefined' && typeof window.DOMParser === 'function') {
          const dp = new window.DOMParser();
          const doc = dp.parseFromString(html, 'text/html');
          const names = ['Alchemy','Blacksmithing','Enchanting','Engineering','Herbalism','Inscription','Jewelcrafting','Leatherworking','Mining','Skinning','Tailoring'];
          const found = new Map(); // name -> { skill, cap }
          const walker = doc.createTreeWalker(doc.body || doc, NodeFilter.SHOW_TEXT, null);
          let node;
          while ((node = walker.nextNode())) {
            const t = (node.nodeValue || '').trim();
            if (!t) continue;
            for (const nm of names) {
              if (t.toLowerCase().includes(nm.toLowerCase())) {
                const candidates = [];
                const textOf = (el) => (el ? (el.textContent || '') : '');
                const parent = node.parentElement;
                if (parent) {
                  candidates.push(textOf(parent));
                  if (parent.parentElement) candidates.push(textOf(parent.parentElement));
                  if (parent.nextElementSibling) candidates.push(textOf(parent.nextElementSibling));
                  if (parent.previousElementSibling) candidates.push(textOf(parent.previousElementSibling));
                }
                const block = [t, ...candidates].join(' ');
                const m2 = block.match(/(\d{1,3})\s*\/\s*(\d{2,3})/);
                if (m2) {
                  const skill = parseInt(m2[1], 10) || 0;
                  const cap = parseInt(m2[2], 10) || 0;
                  const prev = found.get(nm);
                  if (!prev || skill > prev.skill) found.set(nm, { skill, cap });
                }
              }
            }
          }
          found.forEach((val, key) => { primaryCaps.push({ name: key, skill: val.skill, cap: val.cap || 450 }); });
        }
      } catch(_e) {}

      try {
        if (!primaryCaps.length) {
          const names = ['Alchemy','Blacksmithing','Enchanting','Engineering','Herbalism','Inscription','Jewelcrafting','Leatherworking','Mining','Skinning','Tailoring'];
          const lowerHtml = html.toLowerCase();
          for (const nm of names) {
            const idx = lowerHtml.indexOf(nm.toLowerCase());
            if (idx !== -1) {
              const start = Math.max(0, idx - 120);
              const end = Math.min(html.length, idx + 160);
              const context = html.slice(start, end).replace(/<[^>]+>/g, ' ');
              const m2 = context.match(/(\d{1,3})\s*\/\s*(\d{2,3})/);
              if (m2) {
                const skill = parseInt(m2[1], 10) || 0;
                const cap = parseInt(m2[2], 10) || 0;
                primaryCaps.push({ name: nm, skill, cap });
              }
            }
          }
        }
      } catch(_e) {}

      // Parse Recent Activity entries
      const recentActivity = [];
      try {
        // Find a window starting near the Recent Activity header to reduce false positives
        const hdrIdx = html.search(/Recent\s+Activity|Last\s+Activity/i);
        const searchStart = hdrIdx !== -1 ? Math.max(0, hdrIdx - 2000) : 0;
        const searchEnd = hdrIdx !== -1 ? Math.min(html.length, hdrIdx + 40000) : Math.min(html.length, 80000);
        const scope = html.slice(searchStart, searchEnd);
        // Match icon blocks linking to CoT with Warmane CDN icon
        const rowRegex = /<div[^>]*class=["'][^"']*icon[^"']*["'][^>]*>[\s\S]*?<a[^>]+href=["']([^"']*cavernoftime[^"']+)["'][^>]*>[\s\S]*?<img[^>]+src=["'](https?:\/\/cdn\.warmane\.com\/wotlk\/icons\/[^"]+)["'][^>]*>[\s\S]*?<\/a>[\s\S]*?<\/div>/gi;
        let rm;
        const seen = new Set();
        while ((rm = rowRegex.exec(scope)) !== null && recentActivity.length < 15) {
          const cotHref = rm[1];
          const iconUrl = rm[2];
          if (!cotHref || !iconUrl) continue;
          const uniq = cotHref + '|' + iconUrl;
          if (seen.has(uniq)) continue;
          seen.add(uniq);
          // Determine type and id from the CoT href (e.g., /achievement=3010)
          let type = null; let id = null;
          const tm = cotHref.match(/\/(item|achievement|spell|quest)=([0-9]+)/i);
          if (tm) { type = tm[1].toLowerCase(); id = parseInt(tm[2], 10); }
          // Nearby text (title) and timeago
          const context = scope.slice(Math.max(0, rm.index - 400), Math.min(scope.length, rm.index + 1800));
          // Try to find the anchor text for the same href as the title
          let title = null;
          try {
            const hrefEsc = cotHref.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const aRe = new RegExp(`<a[^>]+href=["']${hrefEsc}["'][^>]*>([\\s\\S]*?)<\\/a>`, 'i');
            const am = context.match(aRe);
            if (am && am[1]) {
              const stripped = am[1].replace(/<[^>]+>/g, ' ').replace(/&nbsp;|&amp;/g, ' ').replace(/\s+/g, ' ').trim();
              if (stripped) title = stripped;
            }
            // If we matched the IMG-only anchor (no text), search for a second anchor with text for the same id
            if ((!title || !title.trim()) && type && id != null) {
              const idRe = new RegExp(`<a[^>]+href=["'][^"']*${type}=${id}[^"']*["'][^>]*>(?!\\s*<img)([\\s\\S]*?)<\\/a>`, 'i');
              const im = scope.match(idRe) || context.match(idRe);
              if (im && im[1]) {
                const stripped2 = im[1].replace(/<[^>]+>/g, ' ').replace(/&nbsp;|&amp;/g, ' ').replace(/\s+/g, ' ').trim();
                if (stripped2) title = stripped2;
              }
            }
            // Ultimate fallback: any cavernoftime anchor near here with visible text
            if (!title || !title.trim()) {
              const anyRe = /<a[^>]+href=["'][^"']*cavernoftime[^"']*["'][^>]*>(?!\s*<img)([\s\S]*?)<\/a>/i;
              const anym = context.match(anyRe);
              if (anym && anym[1]) {
                const stripped3 = anym[1].replace(/<[^>]+>/g, ' ').replace(/&nbsp;|&amp;/g, ' ').replace(/\s+/g, ' ').trim();
                if (stripped3) title = stripped3;
              }
            }
          } catch(_e) {}
          // Fallback: text near icon
          if (!title) {
            const after = scope.slice(rm.index, Math.min(scope.length, rm.index + 800));
            const txt = after.replace(/<[^>]+>/g, ' ').replace(/&nbsp;|&amp;/g, ' ').replace(/\s+/g, ' ').trim();
            const m2 = txt.match(/Earned\s+(.*?)(?:\s+achievement|$)/i);
            if (m2) title = (m2[1] || '').trim();
          }
          // time ago
          let timeAgo = null;
          // Prefer a forward search near the icon anchor, but expand the window to catch right-aligned dates
          const forwardWin = scope.slice(rm.index, Math.min(scope.length, rm.index + 4000));
          let mTime = forwardWin.match(/(\d{1,4})\s+(seconds?|minutes?|hours?|days?|weeks?|months?|years?)\s+ago/i);
          if (mTime) {
            timeAgo = `${mTime[1]} ${mTime[2]} ago`;
          } else {
            // Fallback: look around the whole row vicinity (both directions), stripping tags
            const aroundWin = scope.slice(Math.max(0, rm.index - 2000), Math.min(scope.length, rm.index + 4000));
            const textAround = aroundWin.replace(/<[^>]+>/g, ' ');
            const m2 = textAround.match(/(\d{1,4})\s+(seconds?|minutes?|hours?|days?|weeks?|months?|years?)\s+ago/i);
            if (m2) timeAgo = `${m2[1]} ${m2[2]} ago`;
          }
          // Skip generic or header entries without a meaningful title
          if (!title || /^activity$/i.test(title)) {
            continue;
          }
          recentActivity.push({ type: type || 'achievement', id: isNaN(id) ? null : id, iconUrl, cotHref, title, timeAgo });
        }
      } catch(_e) {}

  return { itemAffixes, statsColumns, html, secondarySkills, primaryCaps, recentActivity, modelViewerItems };
    } finally {
      clearTimeout(timeout);
    }
  }

  // Fetch the active spec name from the Warmane Armory talents page.
  async function fetchArmoryTalents(characterName, realmName) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const url = `/armory/character/${encodeURIComponent(characterName)}/${encodeURIComponent(realmName)}/talents`;
      const res = await fetch(url, { signal: controller.signal, headers: { 'Accept': 'text/html' } });
      if (!res.ok) throw new Error(`Armory talents HTTP ${res.status}`);
      const html = await res.text();
      if (!html) return null;

      // DOMParser preferred
      try {
        if (typeof window !== 'undefined' && typeof window.DOMParser === 'function') {
          const dp = new window.DOMParser();
          const doc = dp.parseFromString(html, 'text/html');
          // 1) First attempt the td.selected path (dual-spec pages)
          const td = doc.querySelector('td.selected');
          if (td) {
            const a = td.querySelector('a');
            const specName = a
              ? (a.textContent || '').replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim()
              : (td.textContent || '').replace(/\u00A0/g, ' ').replace(/\s+/g, ' ').trim();
            return specName || null;
          }

          // 2) If no td.selected found (single-layout), parse talent-tree-info blocks
          const infos = Array.from(doc.querySelectorAll('.talent-tree-info'));
          if (infos.length) {
            const specs = infos.map(el => {
              // The inner HTML usually looks like: <span style="font-weight: bold;">SpecName</span> &nbsp;<span>12</span>
              const spans = el.querySelectorAll('span');
              if (spans.length >= 2) {
                const name = (spans[0].textContent || '').replace(/\u00A0/g, ' ').trim();
                const pts = parseInt((spans[1].textContent || '').replace(/[^0-9]/g, ''), 10) || 0;
                return { name, pts };
              }
              // Fallback: try to parse textContent
              const txt = (el.textContent || '').replace(/\u00A0/g, ' ').replace(/&nbsp;/g, ' ').trim();
              const m = txt.match(/^(.*)\s+(\d+)$/);
              if (m) return { name: m[1].trim(), pts: parseInt(m[2], 10) || 0 };
              return null;
            }).filter(Boolean);
            if (specs.length) {
              specs.sort((a, b) => b.pts - a.pts);
              return specs[0].name || null;
            }
          }
          return null;
        }
      } catch (_e) {
        // fall through to regex fallback
      }

      // Regex fallback: try to capture the anchor text following an optional <img>
      const complexRegex = /<td[^>]*class=["']?[^"'>]*\bselected\b[^"'>]*[^>]*>[\s\S]*?<a[^>]*>(?:[\s\S]*?<img[^>]*>\s*)?([\s\S]*?)<\/a>/i;
      const cm = html.match(complexRegex);
      if (cm && cm[1]) {
        const cleaned = cm[1].replace(/<[^>]+>/g, ' ').replace(/&nbsp;|&amp;/g, ' ').replace(/\s+/g, ' ').trim();
        if (cleaned) return cleaned;
      }

      // If no td.selected, try to extract all .talent-tree-info blocks with regex and pick the highest pts
      try {
        const infoRegex = /<div[^>]+class=["']?talent-tree-info[^"'>]*[^>]*>([\s\S]*?)<\/div>/gi;
        const spanRegex = /<span[^>]*>([\s\S]*?)<\/span>/gi;
        const specs = [];
        let im;
        while ((im = infoRegex.exec(html)) !== null) {
          const block = im[1];
          const spans = [];
          let sm;
          while ((sm = spanRegex.exec(block)) !== null) {
            const txt = sm[1].replace(/<[^>]+>/g, '').replace(/&nbsp;|&amp;/g, ' ').replace(/\s+/g, ' ').trim();
            if (txt) spans.push(txt);
          }
          if (spans.length >= 2) {
            const name = spans[0];
            const pts = parseInt(spans[1].replace(/[^0-9]/g, ''), 10) || 0;
            specs.push({ name, pts });
          } else {
            // Try end-of-block text fallback
            const text = block.replace(/<[^>]+>/g, ' ').replace(/&nbsp;|&amp;/g, ' ').replace(/\s+/g, ' ').trim();
            const m = text.match(/^(.*)\s+(\d+)$/);
            if (m) specs.push({ name: m[1].trim(), pts: parseInt(m[2], 10) || 0 });
          }
        }
        if (specs.length) {
          specs.sort((a, b) => b.pts - a.pts);
          return specs[0].name || null;
        }
      } catch (_e) {}

      const tdRegex = /<td[^>]*class=["']?[^"'>]*\bselected\b[^"'>]*[^>]*>([\s\S]*?)<\/td>/i;
      const tdMatch = html.match(tdRegex);
      if (!tdMatch) return null;
      const inside = tdMatch[1];
      const noImg = inside.replace(/<img[^>]*>/gi, ' ');
      const text = noImg.replace(/<[^>]+>/g, '').replace(/&nbsp;|&amp;/g, ' ').replace(/\s+/g, ' ').trim();
      return text || null;
    } finally {
      clearTimeout(timeout);
    }
  }

  // Fetch achievements category (returns HTML either directly or inside a JSON {content})
  async function fetchAchievementsCategory(characterName, realmName, categoryId) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const url = `/armory/character/${encodeURIComponent(characterName)}/${encodeURIComponent(realmName)}/achievements`;
      const body = new URLSearchParams({ category: String(categoryId) }).toString();
      const res = await fetch(url, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Accept': '*/*',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body,
      });
      const text = await res.text();
      if (/^\s*\{/.test(text)) {
        try {
          const json = JSON.parse(text);
          if (json && typeof json.content === 'string') return json.content;
        } catch (_) { /* fallthrough */ }
      }
      return text;
    } finally {
      clearTimeout(timeout);
    }
  }

  // The full search pipeline (data fetch, enrich, calculate, update UI/history)
  const runSearch = async (name, realm) => {
    const data = await fetchCharacterFromAPI(name, realm);
      // Slot assignment by item type using items.json, with placeholders for empty slots
      const SLOT_ORDER = [
        'Head','Neck','Shoulders','Back','Chest','Shirt','Tabard','Wrist',
        'Hand','Waist','Legs','Feet','Ring 1','Ring 2','Trinket 1','Trinket 2',
        'Main hand','Off hand','Relic'
      ];
      const slotMap = Object.fromEntries(SLOT_ORDER.map(s => [s, null]));
      let characterHTML = null;

      function placeInSlot(slotName, eq){
        if(!slotName) return;
        if(slotName === 'Ring 1' || slotName === 'Ring 2'){
          if(!slotMap['Ring 1']) slotMap['Ring 1'] = {...eq, slotType:'Ring 1'}; else slotMap['Ring 2'] = {...eq, slotType:'Ring 2'};
          return;
        }
        if(slotName === 'Trinket 1' || slotName === 'Trinket 2'){
          if(!slotMap['Trinket 1']) slotMap['Trinket 1'] = {...eq, slotType:'Trinket 1'}; else slotMap['Trinket 2'] = {...eq, slotType:'Trinket 2'};
          return;
        }
        // Regular single slot
        slotMap[slotName] = {...eq, slotType: slotName};
      }

      function slotNameForItem(itemRec){
        if(!itemRec) return null;
        const t = itemRec.type;
        switch(t){
          case ItemTypeEnum.Head: return 'Head';
          case ItemTypeEnum.Neck: return 'Neck';
          case ItemTypeEnum.Shoulder: return 'Shoulders';
          case ItemTypeEnum.Cloak: return 'Back';
          case ItemTypeEnum.Chest: return 'Chest';
          case ItemTypeEnum.Shirt: return 'Shirt';
          case ItemTypeEnum.Tabard: return 'Tabard';
          case ItemTypeEnum.Wrist: return 'Wrist';
          case ItemTypeEnum.Hands: return 'Hand';
          case ItemTypeEnum.Waist: return 'Waist';
          case ItemTypeEnum.Legs: return 'Legs';
          case ItemTypeEnum.Boots: return 'Feet';
          case ItemTypeEnum.Ring: return !slotMap['Ring 1'] ? 'Ring 1' : 'Ring 2';
          case ItemTypeEnum.Trinket: return !slotMap['Trinket 1'] ? 'Trinket 1' : 'Trinket 2';
          case ItemTypeEnum.MainHand:
            return !slotMap['Main hand'] ? 'Main hand' : (!slotMap['Off hand'] ? 'Off hand' : 'Off hand');
          case ItemTypeEnum.OffHand:
          case ItemTypeEnum.HeldInOffHand:
            return 'Off hand';
          case ItemTypeEnum.OneHand:
            return !slotMap['Main hand'] ? 'Main hand' : 'Off hand';
          case ItemTypeEnum.TwoHand:
            // Titan's Grip: Warriors may equip 2H in offhand
            if(!slotMap['Main hand']) return 'Main hand';
            if(data.class === 'Warrior' && !slotMap['Off hand']) return 'Off hand';
            return 'Main hand';
          case ItemTypeEnum.Bow:
          case ItemTypeEnum.Crossbow:
          case ItemTypeEnum.Thrown:
          case ItemTypeEnum.Relic:
            return 'Relic';
          default:
            return null;
        }
      }

      if (Array.isArray(data.equipment)) {
        // Place each equipped item into its slot based on items.json
        for(const eq of data.equipment){
          if(!eq || !eq.item) continue;
          const rec = itemsDB.find(r => r.itemID === Number(eq.item));
          const dest = slotNameForItem(rec, slotMap);
          placeInSlot(dest, eq);
        }
        // Build ordered array, filling placeholders
        data.equipment = SLOT_ORDER.map(slot => slotMap[slot] ? slotMap[slot] : { slotType: slot });
      }
      if (data.error) {
        const err = new Error(data.error);
        err.code = 'API_ERROR';
        throw err;
      }

      // Fetch extra details from Armory HTML: enchants, gems, and stats
      try {
        characterHTML = await fetchArmoryExtras(name, realm);

  if (characterHTML) {
          // Attach enchants/gems/transmog and raw rel to equipment by item id
          if (Array.isArray(data.equipment) && characterHTML.itemAffixes) {
            data.equipment = data.equipment.map(eq => {
              if (!eq || !eq.item) return eq;
              const aff = characterHTML.itemAffixes[Number(eq.item)];
              if (aff) {
                return { ...eq, ench: aff.ench || undefined, gems: aff.gems || undefined, transmog: aff.transmog || undefined, rel: aff.rel || undefined };
              }
              return eq;
            });
          }
          // Save stats columns
          if (characterHTML.statsColumns) {
            data.armoryStats = characterHTML.statsColumns;
          }
          // Save recent activity
          if (Array.isArray(characterHTML.recentActivity) && characterHTML.recentActivity.length) {
            data.recentActivity = characterHTML.recentActivity;
          }
          // If the Armory page contained a ModelViewer items array, attach it for the viewer
          if (Array.isArray(characterHTML.modelViewerItems)) {
            try { data.modelViewerEquipment = characterHTML.modelViewerItems; } catch(_) {}
          }
          // Save secondary skills parsed from armory HTML
          if (Array.isArray(characterHTML.secondarySkills) && characterHTML.secondarySkills.length) {
            // Normalize to expected shape: { name, skill } and cap default 450
            data.secondarySkills = characterHTML.secondarySkills.map(s => ({ name: s.name, skill: s.skill, cap: s.cap || 450 }));
          }
          // Merge primary profession caps into existing professions array
          if (Array.isArray(data.professions) && Array.isArray(characterHTML.primaryCaps) && characterHTML.primaryCaps.length) {
            const capMap = new Map();
            characterHTML.primaryCaps.forEach(p => { if (p && p.name) capMap.set(String(p.name).toLowerCase(), p); });
            data.professions = data.professions.map(p => {
              if (!p || !p.name) return p;
              const key = String(p.name).toLowerCase();
              const found = capMap.get(key);
              if (found && (typeof found.cap === 'number')) {
                const skill = (typeof p.skill === 'number' && p.skill > 0) ? p.skill : (typeof found.skill === 'number' ? found.skill : p.skill);
                return { ...p, skill, cap: found.cap };
              }
              return p;
            });
          }
        }
      } catch (e) {
        console.warn('Failed to enrich with Armory extras:', e);
      }

    // Helpers to extract missing gems and enchants from character.equipment without additional fetching
    async function extractMissingGems(character, _html) {
      const result = [];
      if (!character || !Array.isArray(character.equipment)) return result;

      const hasBlacksmithing = Array.isArray(character.professions)
        ? character.professions.map(p => (p || '').toString().toLowerCase()).includes('blacksmithing')
        : false;

      character.equipment.forEach((eq, idx) => {
        if (!eq || !eq.item) return; // skip empty slot

        // Actual gems on item: count non-null/non-undefined gem entries
        const actualGems = Array.isArray(eq.gems)
          ? eq.gems.filter(g => g !== null && g !== undefined && g !== 0).length
          : 0;

        // Expected base sockets from itemsDB
        const rec = itemsDB.find(r => r.itemID === Number(eq.item));
        const expectedBase = rec && typeof rec.gems === 'number' ? rec.gems : 0;

        // Determine slot label to apply special rules
        const slotLabel = eq.slotType || SLOT_ORDER[idx] || 'Unknown';

        // Extra sockets rules:
        // - Belt (Waist) can have 1 extra via belt buckle
        // - Gloves (Hand) or Bracer (Wrist) get +1 if Blacksmithing
        let expected = expectedBase;
        if (slotLabel === 'Waist') {
          expected += 1;
        } else if ((slotLabel === 'Hand' || slotLabel === 'Wrist') && hasBlacksmithing) {
          expected += 1;
        }

        if (expected > actualGems) {
          result.push(slotLabel);
        }
      });
      return result;
    }

    // Recreate GetEnchants logic but using the provided character data (no network calls)
    async function extractMissingEnchants(character, html) {
      const result = [];
      if (!character || !Array.isArray(character.equipment)) return result;

      // Banned indices based on Warmane layout: Neck, Shirt, Tabard, Belt, Trinket 1, Trinket 2
      const bannedIndices = new Set([1, 5, 6, 9, 14, 15]);

      const cls = (character.class || '').toLowerCase();
      const profs = Array.isArray(character.professions) ? character.professions.map(p => (p || '').toString().toLowerCase()) : [];

      character.equipment.forEach((eq, idx) => {
        if (!eq || !eq.item) return; // only consider equipped items
        if (bannedIndices.has(idx)) return; // skip banned slots

        const slotLabel = eq.slotType || SLOT_ORDER[idx] || 'Unknown';
        const hasEnchant = !(eq.ench === null || eq.ench === undefined);
        if (hasEnchant) return; // has enchant -> not missing

        // Special rules:
        // - Ranged (here mapped as 'Relic'): only Hunters require enchant
        if (slotLabel === 'Relic') {
          if (cls.includes('hunter')) {
            result.push(slotLabel);
          }
          return;
        }

        // - Rings require Enchanting profession
        if (slotLabel === 'Ring 1' || slotLabel === 'Ring 2') {
          if (profs.includes('enchanting')) {
            result.push(slotLabel);
          }
          return;
        }

        // - Off hand: casters' frills cannot be enchanted
        if (slotLabel === 'Off hand') {
          if (cls.includes('mage') || cls.includes('warlock') || cls.includes('druid') || cls.includes('priest')) {
            return; // exempt
          }
          // non-caster off-hand can be a weapon -> requires enchant
          result.push(slotLabel);
          return;
        }

        // Default: mark as missing enchant
        result.push(slotLabel);
      });
      return result;
    }

  // 1. GearScore calculation
  let gearScore = await calculateGearScore(data);

  // 2. Missing gems
  let missingGems = await extractMissingGems(data, characterHTML);

  // 3. Missing enchants
  let missingEnchants = await extractMissingEnchants(data, characterHTML);

  // Attach enriched info
  // Try to fetch talents, but do not surface errors (e.g., Warmane HTML 429)
  let specName = null;
  try {
    specName = await fetchArmoryTalents(name, realm);
  } catch(_e) {
    // ignore talents fetch errors; do not fail the whole search
    specName = data.spec || null;
  }

  // Build raid completion table (ICC/RS/TOC/ULDUAR, 25HC 25NM 10HC 10NM)
  let raidProgress = null;
  try {
    const IDS = {
      ICC10: 15041, ICC25: 15042,
      RS10: 14922, RS25: 14923,
      TOC10: 15001, TOC25: 15002,
      ULD10: 14961, ULD25: 14962,
    };
    const [icc10, icc25, rs10, rs25, toc10, toc25, uld10, uld25] = await Promise.all([
      fetchAchievementsCategory(name, realm, IDS.ICC10).catch(()=>''),
      fetchAchievementsCategory(name, realm, IDS.ICC25).catch(()=>''),
      fetchAchievementsCategory(name, realm, IDS.RS10).catch(()=>''),
      fetchAchievementsCategory(name, realm, IDS.RS25).catch(()=>''),
      fetchAchievementsCategory(name, realm, IDS.TOC10).catch(()=>''),
      fetchAchievementsCategory(name, realm, IDS.TOC25).catch(()=>''),
      fetchAchievementsCategory(name, realm, IDS.ULD10).catch(()=>''),
      fetchAchievementsCategory(name, realm, IDS.ULD25).catch(()=>''),
    ]);
    const norm = (s)=> String(s||'').replace(/\\n/g,'\n').replace(/\\\//g,'/').replace(/\s+/g,' ').toLowerCase();
    const hasEarned = (html, pattern) => {
      if(!html) return false;
      const src = norm(html);
      const idx = src.indexOf(pattern);
      if(idx === -1) return false;
      const around = src.slice(idx, Math.min(src.length, idx + 1200));
      return /earned\s+\d{2}\/\d{2}\/\d{4}/i.test(around) || /earned\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(around);
    };
    raidProgress = {
      ICC: {
        '25HC': hasEarned(icc25, 'heroic: fall of the lich king (25 player)'),
        '25NM': hasEarned(icc25, 'fall of the lich king (25 player)'),
        '10HC': hasEarned(icc10, 'heroic: fall of the lich king (10 player)'),
        '10NM': hasEarned(icc10, 'fall of the lich king (10 player)'),
      },
      RS: {
        '25HC': hasEarned(rs25, 'heroic: the twilight destroyer (25 player)'),
        '25NM': hasEarned(rs25, 'the twilight destroyer (25 player)'),
        '10HC': hasEarned(rs10, 'heroic: the twilight destroyer (10 player)'),
        '10NM': hasEarned(rs10, 'the twilight destroyer (10 player)'),
      },
      TOC: {
        '25HC': hasEarned(toc25, 'call of the grand crusade (25 player)'),
        '25NM': hasEarned(toc25, 'call of the crusade (25 player)'),
        '10HC': hasEarned(toc10, 'call of the grand crusade (10 player)'),
        '10NM': hasEarned(toc10, 'call of the crusade (10 player)'),
      },
      ULDUAR: {
        '25HC': hasEarned(uld25, 'alone in the darkness (25 player)'),
        '25NM': hasEarned(uld25, 'the secrets of ulduar (25 player)'),
        '10HC': hasEarned(uld10, 'alone in the darkness (10 player)'),
        '10NM': hasEarned(uld10, 'the secrets of ulduar (10 player)'),
      }
    };
  } catch(_e) { raidProgress = null; }

  const enrichedCharacter = {
    ...data,
    gearScore: gearScore,
    missingGems,
    missingEnchants,
    spec: specName || data.spec || null,
    raidProgress,
  };

  setCharacter(enrichedCharacter);

    // Update history
    const entry = {
      name: data.name,
      realm: data.realm,
      faction: data.faction || data.side || '',
      class: data.class,
      level: data.level,
      race: data.race,
      gearScore: gearScore ?? 0,
      lastChecked: Date.now(),
      ts: Date.now(),
    };
    // Do not record placeholder/unknown searches
    const nameTrim = (entry.name || '').trim();
    const realmTrim = (entry.realm || '').trim();
    if (!nameTrim || nameTrim.toLowerCase() === 'unknown' || !realmTrim || realmTrim.toLowerCase() === 'unknown') {
      setLoading(false);
      return;
    }

  // Prepend the new entry and avoid pruning here; retention is enforced on app load only.
  let newHistory = [entry, ...history.filter((h) => h.name !== entry.name || h.realm !== entry.realm)];
  setHistory(newHistory);
    const serialized = safeStringifyForStorage(newHistory);
    if (serialized !== null) {
      localStorage.setItem("gc-history", serialized);
    } else {
      console.warn('Failed to serialize history; not writing to localStorage to avoid data loss.');
    }
    return enrichedCharacter;
  };

  // Queue helpers
  const updateQueueUi = () => {
    const total = queueRef.current.length + (processingRef.current ? 1 : 0);
    const position = processingRef.current ? 1 : 0; // current running task considered position 1
    const nextAt = queueRef.current.length ? (queueRef.current[0].scheduledAt || Date.now()) : 0;
    setQueueUi({ total, position, nextAt });
  };

  const processNext = async () => {
    // Avoid re-entrancy
    if (processingRef.current) return;
    const now = Date.now();
    // Sort queue by scheduledAt ascending
    queueRef.current.sort((a,b)=> (a.scheduledAt||0) - (b.scheduledAt||0));
    const next = queueRef.current[0];
    if (!next) {
      setLoading(false);
      updateQueueUi();
      return;
    }
    const waitMs = Math.max(0, (next.scheduledAt || now) - now);
    if (waitMs > 0) {
      // Schedule wake-up at the earliest task time
      if (nextTimerRef.current) clearTimeout(nextTimerRef.current);
      nextTimerRef.current = setTimeout(() => { processNext(); }, Math.min(waitMs, 60000));
      updateQueueUi();
      return;
    }
    // Dequeue and process
    queueRef.current.shift();
    processingRef.current = true;
    setLoading(true);
    setError("");
    updateQueueUi();
    const task = next;
    try {
      // Throttle to avoid burst: respect lastSearchTime minimal gap
      const tnow = Date.now();
      const gap = 2000 - (tnow - lastSearchTime);
      if (gap > 0) {
        await new Promise(r => setTimeout(r, gap));
      }
      setLastSearchTime(Date.now());

      const result = await runSearch(task.name, task.realm);
      task.resolve && task.resolve(result);
    } catch (err) {
      // Treat explicit 429 or any error that clearly indicates Too Many Requests as a rate-limit event
      const msg = String((err && err.message) || '').toLowerCase();
      if ((err && err.code === 429) || /\b429\b/.test(msg) || /too\s*many\s*requests/.test(msg)) {
        // Re-schedule the task at the suggested time, do not surface an error
        const delay = Math.max(1000, (err && err.retryAfterMs) || 2000);
        task.attempts = (task.attempts || 0) + 1;
        task.scheduledAt = Date.now() + delay;
        queueRef.current.push(task);
        // Update UI to show countdown to next attempt
        updateQueueUi();
      } else {
        console.error("Search error:", err);
        let errorMessage = err && err.message ? err.message : 'Unknown error';
        if (err && err.name === 'AbortError') {
          errorMessage = "Request timed out. The server may be busy. Please try again.";
        } else if (err && (String(err.message || '').includes("Failed to fetch") || String(err.message || '').includes("NetworkError"))) {
          errorMessage = "Network error. Please check your connection and try again.";
        } else if (err && String(err.message || '').includes("timeout")) {
          errorMessage = "Request timed out. The server may be busy. Please try again.";
        }
        else errorMessage = "Something went wrong. Please try again later.";

        setError(errorMessage);
        setCharacter(null);
        task.reject && task.reject(err);
      }
    } finally {
      processingRef.current = false;
      // Chain to next task (or rescheduled task)
      processNext();
    }
  };

  const enqueueSearch = (name, realm) => {
    // Create a task and return a promise that resolves/rejects when done
    return new Promise((resolve, reject) => {
      const task = { id: `${Date.now()}_${Math.random().toString(36).slice(2)}`, name, realm, attempts: 0, scheduledAt: Date.now(), resolve, reject };
      queueRef.current.push(task);
      updateQueueUi();
      processNext();
    });
  };

  // Preserve existing API for SearchBar by delegating to enqueueSearch
  const handleSearch = async (name, realm) => {
    return enqueueSearch(name, realm);
  };

  const handleHistoryClick = async (entry) => {
    // Signal CharacterPanel (and other listeners) to collapse any expanded raid rows immediately
    try { setCollapseRaidsSignal(s => (s || 0) + 1); } catch(_e){}
    try {
      await enqueueSearch(entry.name, entry.realm);
    } catch (err) {
      console.error("Error during history click:", err);
    }
  };

  const handleRemoveFromHistory = (entry) => {
    if (window.confirm(`Are you sure you want to remove "${entry.name}" from "${entry.realm}" from your search history?`)) {
      const newHistory = history.filter(h => !(h.name === entry.name && h.realm === entry.realm));
      setHistory(newHistory);
      const serialized = safeStringifyForStorage(newHistory);
      if (serialized !== null) {
        localStorage.setItem("gc-history", serialized);
      } else {
        console.warn('Failed to serialize history; not writing to localStorage to avoid data loss.');
      }
      // If the removed entry is the currently displayed character, close the character panel
      if (character && character.name === entry.name && character.realm === entry.realm) {
        setCharacter(null);
        setError("");
        setLoading(false);
      }
    }
  };

  const renderLastChecked = (timestamp) => {
    if (!timestamp || timestamp === "Never") return "Never";

    const parsed = new Date(timestamp);

    if (isNaN(parsed)) {
      return "Invalid date";
    }

    return (
      <span title={parsed.toLocaleString()}>
        {formatDistanceToNow(parsed, { addSuffix: true })}
      </span>
    );
  };

  function deepClone(obj, seen = new WeakMap()) {
    if (obj === null || typeof obj !== "object") {
      return obj;
    }

    if (seen.has(obj)) {
      return '[Circular Reference]';
    }

    const clone = Array.isArray(obj) ? [] : {};
    seen.set(obj, clone);

    try {
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          clone[key] = deepClone(obj[key], seen);
        }
      }
    } catch (error) {
      console.error('Error in deepClone:', error);
      return '[Error cloning]';
    }

    return clone;
  }

  // Safe stringify function for localStorage that handles circular references
  const safeStringifyForStorage = (obj) => {
    const seen = new WeakSet();
    
    const replacer = (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular Reference]';
        }
        seen.add(value);
      }
      return value;
    };
    
    try {
      return JSON.stringify(obj, replacer);
    } catch (error) {
      console.error('Error stringifying for storage:', error);
      return null; // serialization failed -- do not write fallback to avoid data loss
    }
  };

  async function calculateGearScore(character) {
    let gearScore = 0;
    let equippedItems = [];
    let weapons = [];

    if (!character || !character.equipment || character.equipment.length === 0) {
      return 0;
    }

    // Ensure PVPGear is initialized
    if (!Array.isArray(character.PVPGear)) {
      character.PVPGear = [];
    }

    character.equipment.forEach(item => {
      equippedItems.push(Number(item.item));
    });

    equippedItems.forEach(equippedItem => {
      const item = itemsDB.find(element => element.itemID === equippedItem);
      if (!item) return;

      // PVP Gear
      if (item.PVP === 1) {
        character.PVPGear.push(ItemTypeEnumToString(item.type) + ": " + item.name);
      }

      // Hunter ranged scaling
      const hunterWeaponTypes = [
        ItemTypeEnum["OneHand"],
        ItemTypeEnum["TwoHand"],
        ItemTypeEnum["MainHand"],
        ItemTypeEnum["OffHand"]
      ];

      if (character.class === "Hunter" && item.type === 26) {
        gearScore += Math.round(item.GearScore * 5.3224);
      } else if (character.class === "Hunter" && hunterWeaponTypes.includes(item.type)) {
        gearScore += Math.round(item.GearScore * 0.3164);
      } else if (item.class === 2 && (item.subclass === 1 || item.subclass === 5 || item.subclass === 8)) {
        weapons.push(item.GearScore);
      } else {
        gearScore += item.GearScore;
      }
    });

    // Titan's Grip handling
    if (weapons.length === 2) {
      gearScore += Math.floor((weapons[0] + weapons[1]) / 2);
    } else if (weapons.length === 1) {
      gearScore += weapons[0];
    }

    return Math.ceil(gearScore);
  }
  
  return (
    <div className="app-bg">
      <QueueBar total={queueUi.total} position={queueUi.position} nextAt={queueUi.nextAt} />
        <div className="main-container">
        <SearchBar onSearch={handleSearch} loading={loadingDerived} />
        <HistoryPanel
          history={history.map((entry) => {
            try {
              const sanitizedEntry = deepClone(entry); // Use custom deepClone to handle circular references
              return {
                ...sanitizedEntry,
                lastCheckedDisplay: renderLastChecked(sanitizedEntry.lastChecked),
              };
            } catch (error) {
              console.error('Error sanitizing history entry:', error);
              // Return a safe fallback entry
              return {
                name: entry.name || 'Unknown',
                realm: entry.realm || 'Unknown',
                class: entry.class || 'Unknown',
                level: entry.level || 0,
                race: entry.race || 'Unknown',
                gearScore: entry.gearScore || 'Unknown',
                lastChecked: entry.lastChecked || 'Never',
                lastCheckedDisplay: renderLastChecked(entry.lastChecked),
              };
            }
          })}
          onClick={handleHistoryClick}
          onRemove={handleRemoveFromHistory}
        />
        <CharacterPanel character={character} error={error} loading={loading} collapseSignal={collapseRaidsSignal} />
      </div>
    </div>
  );
}

export default Search;